<template>
  <div
    class="modal"
    :class="{ 'is-active': value }">

    <div class="modal-background"
      @click="close()">
    </div>
    
    <div class="modal-content is-wide">
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Bulk Adjust Item Prices</p>
          <button
            class="delete"
            aria-label="close"
            @click="close(true)">
          </button>
        </header>

        <v-form
          class="modal-card-body" 
          :valid.sync="valid">

          <div class="is-size-7 mb-3 is-italic">
            Note: Only items that are checked will be adjusted.
          </div>

          <div class="tabs mb-3 mt-2">
            <ul>
              <li 
                v-if="materials.length > 0"
                @click="active = 'materials'"
                :class="{ 'is-active': active == 'materials' }">
                <a>Materials</a>
              </li>       
              <li 
                v-if="processings.length > 0"
                @click="active = 'processings'"
                :class="{ 'is-active': active == 'processings' }">
                <a>Processings</a>
              </li>
              <li 
                v-if="fabrications.length > 0"
                @click="active = 'fabrications'"
                :class="{ 'is-active': active == 'fabrications' }">
                <a>Fabrications</a>
              </li> 
              <li 
                v-if="installations.length > 0"
                @click="active = 'installations'"
                :class="{ 'is-active': active == 'installations' }">
                <a>Installations</a>
              </li>
            </ul>
          </div>

          <div v-if="active == 'materials'" key="materials">
            <table class="table is-fullwidth has-small-font">
              <thead>
                <tr>
                  <th></th>
                  <th>Category</th>
                  <th>Material</th>
                  <th>Unit Price</th>
                  <th>Price Adjustment</th>
                  <th>Adjusted Price</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="material in materials">
                  <tr 
                    class="v-centered subheader has-background-light"
                    :key="material.id">
                    <td>
                      <v-checkbox 
                        v-model="material.change" 
                        @input="toggleAllItems('material', material.id, $event)"/>
                    </td>
                    <td>
                      {{ material.product_master.product_category.category }}
                    </td>
                    <td>
                      {{ material.product_master.name }}
                    </td>
                    <td>
                      {{ material.unit_price | currency(2) }}
                    </td>
                    <td>
                      <v-text-field 
                        type="number"
                        placeholder="Unit Price Adjustment"
                        required
                        v-model="material.unit_price_adj"
                        @input="material.change = true; toggleAllItems('material', material.id, true)"/>
                    </td>
                    <td>
                      {{ (+material.unit_price + +material.unit_price_adj) | currency(2) }}
                    </td>
                  </tr>
                  <tr 
                    class="v-centered" 
                    v-for="item in material.items" 
                    :key="`material-${material.id}-item-${item.id}`">
                    <td>
                      <v-checkbox 
                        v-model="item.change"
                        @input="adjust('material', material.id, $event)"/>
                    </td>
                    <td colspan="5">{{ item.architecture_name }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <div v-if="active == 'processings'" key="processings">
            <table class="table is-fullwidth has-small-font">
              <thead>
                <tr>
                  <th></th>
                  <th>Processing</th>
                  <th>Unit Price</th>
                  <th>Price Adjustment</th>
                  <th>Adjusted Price</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="processing in processings">
                  <tr 
                    class="v-centered subheader has-background-light"
                    :key="processing.id">
                    <td>
                      <v-checkbox 
                        v-model="processing.change"
                        @input="toggleAllItems('processing', processing.id, $event)"/>
                    </td>
                    <td>
                      {{ processing.processing.processing_name }}
                    </td>
                    <td>
                      {{ processing.unit_price | currency(2) }}
                    </td>
                    <td>
                      <v-text-field 
                        type="number"
                        placeholder="Unit Price Adjustment"
                        required
                        v-model="processing.unit_price_adj"
                        @input="processing.change = true; toggleAllItems('processing', processing.id, true)"/>
                    </td>
                    <td>
                      {{ (+processing.unit_price + +processing.unit_price_adj) | currency(2) }}
                    </td>
                  </tr>
                  <tr class="v-centered" 
                    v-for="item in processing.items" 
                    :key="`processing-${processing.id}-item-${item.id}`">
                    <td>
                      <v-checkbox v-model="item.change"
                        @input="adjust('processing', processing.id, $event)"/>
                    </td>
                    <td colspan="4">{{ item.architecture_name }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <div v-if="active == 'fabrications'" key="fabrications">
            <table class="table is-fullwidth has-small-font">
              <thead>
                <tr>
                  <th></th>
                  <th>Fabrication Category</th>
                  <th>Fabrication</th>
                  <th>Unit Price</th>
                  <th>Price Adjustment</th>
                  <th>Adjusted Price</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="fabrication in fabrications">
                  <tr 
                    class="v-centered subheader has-background-light"
                    :key="fabrication.id">
                    <td>
                      <v-checkbox 
                        v-model="fabrication.change"
                        @input="toggleAllItems('fabrication', fabrication.id, $event)"/>
                    </td>
                    <td>
                      {{ fabrication.fabrication.fabrication_type.fabrication_type_name }}
                    </td>
                    <td>
                      {{ fabrication.fabrication.fabrication_name }}
                    </td>
                    <td>
                      {{ fabrication.unit_price | currency(2) }}
                    </td>
                    <td>
                      <v-text-field 
                        type="number"
                        placeholder="Unit Price Adjustment"
                        required
                        v-model="fabrication.unit_price_adj"
                        @input="fabrication.change = true; toggleAllItems('fabrication', fabrication.id, true)"/>
                    </td>
                    <td>
                      {{ (+fabrication.unit_price + +fabrication.unit_price_adj) | currency(2) }}
                    </td>
                  </tr>
                  <tr 
                    class="v-centered" 
                    v-for="item in fabrication.items" 
                    :key="`fabrication-${fabrication.id}-item-${item.id}`">
                    <td>
                      <v-checkbox v-model="item.change"
                        @input="adjust('fabrication', fabrication.id, $event)"/>
                    </td>
                    <td colspan="5">{{ item.architecture_name }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <div v-if="active == 'installations'" key="installations">
            <table class="table is-fullwidth has-small-font">
              <thead>
                <tr>
                  <th></th>
                  <th>Installation Category</th>
                  <th>Installation</th>
                  <th>Unit Price</th>
                  <th>Price Adjustment</th>
                  <th>Adjusted Price</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="installation in installations">
                  <tr 
                    class="v-centered subheader has-background-light"
                    :key="installation.id">
                    <td>
                      <v-checkbox 
                        v-model="installation.change"
                        @input="toggleAllItems('installation', installation.id, $event)"/>
                    </td>
                    <td>
                      {{ installation.installation.installation_type.installation_type_name }}
                    </td>
                    <td>
                      {{ installation.installation.installation_name }}
                    </td>
                    <td>
                      {{ installation.unit_price | currency(2) }}
                    </td>
                    <td>
                      <v-text-field 
                        type="number"
                        placeholder="Unit Price Adjustment"
                        required
                        v-model="installation.unit_price_adj"
                        @input="installation.change = true; toggleAllItems('installation', installation.id, true)"/>
                    </td>
                    <td>
                      {{ (+installation.unit_price + +installation.unit_price_adj) | currency(2) }}
                    </td>
                  </tr>
                  <tr 
                    class="v-centered" 
                    v-for="item in installation.items" 
                    :key="`installation-${installation.id}-item-${item.id}`">
                    <td>
                      <v-checkbox v-model="item.change"
                        @input="adjust('installation', installation.id, $event)"/>
                    </td>
                    <td colspan="5">{{ item.architecture_name }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

        </v-form>

        <footer class="modal-card-foot">
          <button 
            :disabled="!valid || saving"
            class="button is-primary"
            :class="{ 'is-loading': saving }"
            @click.stop="saveChanges()">
            Save
          </button>
          <button 
            class="button" 
            @click.stop="close(true)">
            Cancel
          </button>
        </footer>

      </div>
    </div>
  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import VCheckbox from '@/components/base/VCheckbox'
import VForm from '@/components/base/VForm'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean
    }
  },
  components: { VTextField, VCheckbox, VForm },
  data() {
    return {
      active: 'materials',
      valid: true,
      saving: false,
      materials: [],
      processings: [],
      fabrications: [],
      installations: []
    }
  },
  computed: {
    ...mapState("cladifyEstimation", ["estimation"])
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("cladifyEstimation", [
      "updateItemInstallation", "updateItemMaterial", "updateItemProcessing",
      "updateItemFabrication", "getEstimation"
    ]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      if (this.estimation) {
        const { estimation, materials, processings, fabrications, installations } = this;

        estimation.items.forEach(item => {
          item.materials.forEach(material => {
            let index = materials.map(d => d.product_master_id).indexOf(material.product_master_id);
            let copy = Object.assign({}, material);
            if (index == -1) {
              copy.change = false;
              copy.items = [{ 
                id: item.id, 
                item_material_id: material.id, 
                architecture_name: item.architecture_name, 
                change: false }];
              materials.push(copy);
            } else {
              materials[index].items.push({ 
                id: item.id, 
                item_material_id: material.id, 
                architecture_name: item.architecture_name, 
                change: false });
            }
          })
        })
        
        estimation.items.forEach(item => {
          item.processing.forEach(pr => {
            let index = processings.map(d => d.processing_id).indexOf(pr.processing_id);
            let copy = Object.assign({}, pr);
            if (index == -1) {
              copy.change = false;
              copy.items = [{ 
                id: item.id, 
                item_processing_id: pr.id, 
                architecture_name: item.architecture_name, 
                change: false }];
              processings.push(copy);
            } else {
              processings[index].items.push({ 
                id: item.id, 
                item_processing_id: pr.id,
                architecture_name: item.architecture_name, 
                change: false });
            }
          })
        })

        estimation.items.forEach(item => {
          item.fabrications.forEach(fabrication => {
            let index = fabrications.map(d => d.fabrication_id).indexOf(fabrication.fabrication_id);
            let copy = Object.assign({}, fabrication);
            if (index == -1) {
              copy.change = false;
              copy.items = [{ 
                id: item.id, 
                item_fabrication_id: fabrication.id,
                architecture_name: item.architecture_name, 
                change: false }];
              fabrications.push(copy);
            } else {
              fabrications[index].items.push({ 
                id: item.id, 
                item_fabrication_id: fabrication.id,
                architecture_name: item.architecture_name, 
                change: false });
            }
          })
        })

        estimation.items.forEach(item => {
          item.installations.forEach(installation => {
            let index = installations.map(d => d.installation_id).indexOf(installation.installation_id);
            let copy = Object.assign({}, installation);
            if (index == -1) {
              copy.change = false;
              copy.items = [{ 
                id: item.id, 
                item_installation_id: installation.id,
                architecture_name: item.architecture_name, 
                change: false }];
              installations.push(copy);
            } else {
              installations[index].items.push({ 
                id: item.id, 
                item_installation_id: installation.id,
                architecture_name: item.architecture_name, 
                change: false });
            }
          })
        })
      }
    },
    toggleAllItems(type, id, v) {
      if (type == 'material') {
        this.materials.forEach(material => {
          if (material.id == id) {
            material.items.forEach(item => {
              item.change = v;
            })
          }
        })
      } else if (type == 'processing') {
        this.processings.forEach(processing => {
          if (processing.id == id) {
            processing.items.forEach(item => {
              item.change = v;
            })
          }
        })
      } else if (type == 'fabrication') {
        this.fabrications.forEach(fabrication => {
          if (fabrication.id == id) {
            fabrication.items.forEach(item => {
              item.change = v;
            })
          }
        })
      } else {
        this.installations.forEach(installation => {
          if (installation.id == id) {
            installation.items.forEach(item => {
              item.change = v;
            })
          }
        })
      }
    },
    adjust(type, id, v) {
      if (type == 'material') {
        this.materials.forEach(material => {
          if (material.id == id) {
            if (v) {
              material.change = true;
            } else {
              if (!material.items.map(d => d.change).includes(true)) {
                material.change = false;
              }
            }
          }
        })
      } else if (type == 'processing') {
        this.processings.forEach(processing => {
          if (processing.id == id) {
            if (v) {
              processing.change = true;
            } else {
              if (!processing.items.map(d => d.change).includes(true)) {
                processing.change = false;
              }
            }
          }
        })
      } else if (type == 'fabrication') {
        this.fabrications.forEach(fabrication => {
          if (fabrication.id == id) {
            if (v) {
              fabrication.change = true;
            } else {
              if (!fabrication.items.map(d => d.change).includes(true)) {
                fabrication.change = false;
              }
            }
          }
        })
      } else {
        this.installations.forEach(installation => {
          if (installation.id == id) {
            if (v) {
              installation.change = true;
            } else {
              if (!installation.items.map(d => d.change).includes(true)) {
                installation.change = false;
              }
            }
          }
        })
      }
    },
    saveChanges() {
      this.saving = true;
      let promises = [];

      this.materials.forEach(material => {
        if (material.change) {
          material.items.forEach(item => {
            if (item.change) {
              promises.push(
                this.updateItemMaterial({
                  estimationId: this.estimation.id,
                  itemMaterialId: item.item_material_id,
                  data: { unit_price_adj: material.unit_price_adj },
                  update: false
                })
              )
            }
          })
        }
      })

      this.processings.forEach(processing => {
        if (processing.change) {
          processing.items.forEach(item => {
            if (item.change) {
              promises.push(
                this.updateItemProcessing({
                  estimationId: this.estimation.id,
                  itemProcessingId: item.item_processing_id,
                  data: { unit_price_adj: processing.unit_price_adj },
                  update: false
                })
              )
            }
          })
        }
      })

      this.fabrications.forEach(fabrication => {
        if (fabrication.change) {
          fabrication.items.forEach(item => {
            if (item.change) {
              promises.push(
                this.updateItemFabrication({
                  estimationId: this.estimation.id,
                  itemfabricationId: item.item_fabrication_id,
                  data: { unit_price_adj: fabrication.unit_price_adj },
                  update: false
                })
              )
            }
          })
        }
      })

      this.installations.forEach(installation => {
        if (installation.change) {
          installation.items.forEach(item => {
            if (item.change) {
              promises.push(
                this.updateItemInstallation({
                  estimationId: this.estimation.id,
                  iteminstallationId: item.item_installation_id,
                  data: { unit_price_adj: installation.unit_price_adj },
                  update: false
                })
              )
            }
          })
        }
      })

      Promise.all(promises).then(() => {
        this.getEstimation(this.estimation.id).then(() => {
          this.saving = false;
          this.setNotification({
            status: true,
            type: 'success',
            message: 'Prices successfully updated'
          })
        });

        this.close();
      }).catch(() => {
        this.saving = false;
      })
    },
    close() {
      this.$emit('input', false);
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/table.scss';
@import '@/styles/modal.scss';

.capitalize {
  text-transform: capitalize;
}
</style>
